import { SITE } from "../lib/site";
import { Check, X, ArrowUpRight, Sparkles } from "lucide-react";

function ServiceCard({ s, index }) {
  return (
    <article
      data-testid={`service-card-${s.id}`}
      className={`reveal group relative overflow-hidden border border-white/10 bg-[#0a0a0a] glow-card rounded-sm ${
        s.popular ? "lg:scale-[1.02] lg:-translate-y-2" : ""
      }`}
    >
      {/* Image */}
      <div className="relative h-56 overflow-hidden">
        <img
          src={s.image}
          alt={s.name}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.4s] group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-black/30 to-transparent" />
        {s.popular && (
          <span
            data-testid={`service-popular-${s.id}`}
            className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-gold text-black px-3 py-1 text-[10px] uppercase tracking-[0.22em] rounded-full font-medium"
          >
            <Sparkles size={12} /> Most Popular
          </span>
        )}
        <span className="absolute top-4 right-4 font-display text-white/40 text-sm">
          0{index + 1}
        </span>
      </div>

      {/* Body */}
      <div className="p-8 lg:p-10 flex flex-col h-full">
        <div className="flex items-baseline justify-between gap-4">
          <h3
            data-testid={`service-name-${s.id}`}
            className="font-display text-3xl lg:text-4xl text-white tracking-tight"
          >
            {s.name}
          </h3>
          <div className="text-right shrink-0">
            <div
              data-testid={`service-price-${s.id}`}
              className="font-display text-3xl text-gold"
            >
              ${s.price}
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mt-1">
              {s.duration}
            </div>
          </div>
        </div>
        {s.subtitle && (
          <div className="mt-2 text-sm text-white/55 italic">{s.subtitle}</div>
        )}

        <p className="mt-5 text-white/65 leading-relaxed">{s.description}</p>

        <ul className="mt-7 space-y-3">
          {s.features.map((f, i) => (
            <li
              key={i}
              data-testid={`service-feature-${s.id}-${i}`}
              className="flex items-start gap-3 text-white/80 text-sm"
            >
              <Check size={16} className="text-gold mt-0.5 shrink-0" />
              <span>{f}</span>
            </li>
          ))}
        </ul>

        {s.excluded && s.excluded.length > 0 && (
          <>
            <div className="mt-7 flex items-center gap-3">
              <span className="h-px flex-1 bg-white/10" />
              <span className="text-[10px] uppercase tracking-[0.24em] text-white/40">
                Not Included
              </span>
              <span className="h-px flex-1 bg-white/10" />
            </div>
            <ul className="mt-4 space-y-2.5">
              {s.excluded.map((x, i) => (
                <li
                  key={i}
                  data-testid={`service-excluded-${s.id}-${i}`}
                  className="flex items-start gap-3 text-white/45 text-sm"
                >
                  <X size={16} className="text-white/30 mt-0.5 shrink-0" />
                  <span>{x}</span>
                </li>
              ))}
            </ul>
          </>
        )}

        <a
          href={SITE.bookingUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`service-book-${s.id}`}
          className="mt-10 inline-flex items-center justify-between gap-3 px-6 py-4 border border-white/15 text-white rounded-sm uppercase text-xs tracking-[0.22em] hover:border-gold hover:text-gold transition-all"
        >
          Book {s.name}
          <ArrowUpRight size={16} />
        </a>
      </div>
    </article>
  );
}

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-28 lg:py-36 bg-black"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-10 mb-16 reveal">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-8 bg-gold" />
              <span className="text-xs uppercase tracking-[0.32em] text-gold/90">
                The Packages
              </span>
            </div>
            <h2 className="font-display font-light text-5xl lg:text-6xl tracking-tight max-w-2xl">
              Three ways to make your car
              <span className="italic gold-shimmer"> feel new again</span>.
            </h2>
          </div>
          <p className="lg:max-w-sm text-white/60 leading-relaxed">
            Drop your car at our Pinehurst / Northgate location — or add a
            mobile service and we&apos;ll come to you in Northeast Seattle.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 items-stretch">
          {SITE.services.map((s, i) => (
            <ServiceCard key={s.id} s={s} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
